import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { HpmsTextHtmlAPI } from '@philips-emr/hpms-provider-accreditation-components';
import { PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnClick, OnClose, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services';

import BaseController from '../BaseController';
import './privacy.css';

interface TermOfUse {
  status: Status;
  dsTerm: string;
}

interface PrivacyWarning {
  status: Status;
  dsTerm: string;
}

enum Status {
  N = 'N',
  S = 'S',
}

@Controller({ fragment: 'privacy' })
export default class PrivacyController extends BaseController {

  @Inject('panel-term')
  private panelTerm: PanelAPI;

  @Inject('panel-privacy-warning')
  private panelPrivacyWarning: PanelAPI;

  @Inject('panel-modal-description')
  private panelModal: PanelAPI;

  @Inject('panel-privacy-warning-text')
  private panelPrivacyWarningText: PanelAPI;

  @Inject('panel-term-text')
  private panelTermText: PanelAPI;

  @Inject('hpms-text-html-privacy-warning')
  private hpmsTextPrivacyWarning: HpmsTextHtmlAPI;

  @Inject('hpms-text-html-term')
  private hpmsTextTermOfUse: HpmsTextHtmlAPI;

  @OnDynamicContentRender('hpms-dynamic-content-field')
  onDynamicContentRenderPrivacyWarning(event: RenderEvent): void {
    const { data, render } = event;
    const { status } = data as PrivacyWarning;
    if (status) {
      render((): ReactElement => <Stamp icon={this.getIconByStatus(status)} color={this.getColorByStatus(status)} label={this.getLabelByStatus(status)} />);
    }
  }

  @OnDynamicContentRender('hpms-dynamic-content-field-term')
  onDynamicContentRenderTermOfUse(event: RenderEvent): void {
    const { data, render } = event;
    const { status } = data as TermOfUse;
    if (status) {
      render((): ReactElement => <Stamp icon={this.getIconByStatus(status)} color={this.getColorByStatus(status)} label={this.getLabelByStatus(status)} />);
    }
  }

  @OnAfterFetch('fetch-privacy-warning')
  onAfterFetchPrivacyWarning(event: FetchEvent): void {
    if (event.error) {
      this.panelPrivacyWarning.setVisible(false);
    }
    else if (!event.data) {
      this.panelPrivacyWarning.setVisible(false);
    }
    else {
      const data = event.data as PrivacyWarning;
      const { dsTerm } = data;
      const descriptionPrivacyWarning = dsTerm;
      this.context.set({ descriptionPrivacyWarning });
    }
  }

  @OnAfterFetch('fetch-term')
  onAfterFetchTerm(event: FetchEvent): void {
    if (event.error) {
      this.panelTerm.setVisible(false);
    }
    else if (!event.data) {
      this.panelTerm.setVisible(false);
    }
    else {
      const data = event.data as TermOfUse;
      const { dsTerm } = data;
      const descriptionTermOfUse = dsTerm;
      this.context.set({ descriptionTermOfUse });
    }
  }

  @OnClick('card-privacy-warning')
  onClickCardPrivacyWarning(): void {
    this.panelModal.setVisible(true);
    this.context.set({ isPrivacyWarning: true });
  }

  @OnClick('card-term')
  onClickCardTerm(): void {
    this.panelModal.setVisible(true);
    this.context.set({ isTerm: true });
  }

  @OnMount('modal-description')
  onMountModal(): void {
    const isPrivacyWarning = this.context.get('isPrivacyWarning') as boolean;
    const isTerm = this.context.get('isTerm') as boolean;
    if (isPrivacyWarning) {
      this.panelPrivacyWarningText.setVisible(true);
    }
    if (isTerm) {
      this.panelTermText.setVisible(true);
    }
  }

  @OnMount('field-privacy-warning-text')
  onMountAvisoTexto(): void {
    const descriptionPrivacyWarning = this.context.get('descriptionPrivacyWarning') as string;
    this.hpmsTextPrivacyWarning.setData(descriptionPrivacyWarning);
  }

  @OnMount('field-term-text')
  onMountTermoTexto(): void {
    const descriptionTermOfUse = this.context.get('descriptionTermOfUse') as string;
    this.hpmsTextTermOfUse.setData(descriptionTermOfUse);
  }

  @OnClose('modal-description')
  onCloseModal(): void {
    this.panelModal.setVisible(false);
    this.context.set({ isPrivacyWarning: false });
    this.context.set({ isTerm: false });
  }

  private getIconByStatus(status: Status): StampIcon {
    const icon = {
      [Status.N]: StampIcon.CHECK,
      [Status.S]: StampIcon.CANCELED,
    };

    return icon[status];
  }

  private getColorByStatus(status: Status): Color {
    const color = {
      [Status.N]: Color.SAFETY_GREEN,
      [Status.S]: Color.SAFETY_RED,
    };

    return color[status];
  }

  private getLabelByStatus(status: Status): string {
    const message = (status == Status.N) ? 'pa.in-force' : 'pa.overdue';
    return msg(message);
  }
}
